import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Calendar, X } from "lucide-react";
import { cn } from "../lib/utils";

const fmt = (s) => {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * Compact "Start"/"End" date chip with popover editor. Optimistic — calls onSave(yyyy-mm-dd | null).
 *
 * Props:
 *   value: ISO date string (yyyy-mm-dd) or null
 *   onSave(newValue): async/sync. Receives new yyyy-mm-dd string or null when cleared.
 *   placeholder: "Start" | "End" | etc.
 *   tone: "blue" (default) | "purple"
 *   readOnly: bool — render plain badge with no popover when true
 */
export default function InlineDatePicker({ value, onSave, placeholder = "Date", tone = "blue", readOnly = false, label }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const isSet = !!value;
  const tones = {
    blue: isSet ? "bg-blue-900/30 border-blue-700/50 text-blue-300" : "bg-zinc-800/50 border-zinc-700 text-zinc-500",
    purple: isSet ? "bg-purple-900/30 border-purple-700/50 text-purple-300" : "bg-zinc-800/50 border-zinc-700 text-zinc-500",
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft || null);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await onSave(null);
      setDraft("");
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const trigger = (
    <Badge
      variant="outline"
      className={cn(
        "h-6 px-1.5 gap-1 text-[10px] font-normal",
        tones[tone],
        !readOnly && "cursor-pointer hover:bg-zinc-700/40"
      )}
      onClick={(e) => {
        if (readOnly) return;
        e.stopPropagation();
      }}
    >
      <Calendar className="h-3 w-3" />
      {fmt(value) || placeholder}
    </Badge>
  );

  if (readOnly) return trigger;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="w-56 bg-zinc-900 border-zinc-700 p-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-2">
          <Label className="text-xs text-zinc-400">{label || placeholder}</Label>
          <Input
            type="date"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm"
          />
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              className="h-7 text-xs flex-1 bg-blue-600 hover:bg-blue-700"
              disabled={saving || draft === (value || "")}
              onClick={handleSave}
            >
              {saving ? "..." : "Save"}
            </Button>
            {isSet && (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                disabled={saving}
                onClick={handleClear}
                title="Clear date"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
